import { defalutModalContentProps } from '@components/Modal/ModalProvider';
import styles from './EjectionFractionModalContent.module.scss';
import { ReactComponent as Close } from 'img/btn-close.svg';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { useEffect, useState } from 'react';
import useSeriesGroup from '@hooks/Series/useSeriesGroup';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { Series } from '@models/Series';
import Radio from '@components/Radio';
import Button from '@components/Button';
import Dropdown from '@components/Dropdown';
import {
  send_ef_info,
} from '@components/EjectionFractionUtilities/EjectionFractionUtilities';
import useAuth from '@hooks/Auth/useAuth';
import useModalActions from '@hooks/common/useModalAction';
import { fetch_base_info } from 'utils/AnalysisBaseUtilities/AnalysisBaseUtilities';
import { calculateRRPercent } from '@hooks/useAnalysisForPanel/useDicomUtils';
import { requestBufferPoolJson, useBufferPool } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';

interface EjectionFractionProps extends defalutModalContentProps {
  series: Series;
  onComplete?: (ef_result_info: any, ef_value: any, data: string[][]) => void;
  segmentationStatusBySeries?: Record<string, boolean>;
}
interface EjectionFractionInfo{
  id:string;
  percent:string;
}

const EjectionFractionModalContent: React.FC<EjectionFractionProps> = ({
  close,
  series,
  onComplete,
  segmentationStatusBySeries,
}) => {
  const { selectedStudyId, selectedSeriesId } = useBrowserPage();
  const { updateSelectAnalysis } = useAnalysisPage();
  const { updateSeriesSections, seriesSelections } = useBrowserPage();
  const { accessToken } = useAuth();
  const { successAction } = useModalActions();
  const [selectedSeries, setSelectedSeries] = useState<
    EjectionFractionInfo[]
  >([]);
  const [segmentationStatusMap, setSegmentationStatusMap] = useState<
    Record<string, boolean>
  >({});
  const {bufferPool,setBufferPool} = useBufferPool();
  const pool = bufferPool[selectedSeriesId!];

  const options = Array.from({ length: 21 }, (_, i) => {
    const value = i * 5;
    return { value: `${value}`, label: `${value}%` };
  });
  const { Series, isSuccess } = useSeriesGroup({
    study_id: selectedStudyId ?? '',
  });

  useEffect(() => {
    if (seriesSelections.length > 0) {
      setSelectedSeries(
        seriesSelections.map(sel => {
          const series = Series.data.find(s => s.id === sel.id)
          const phase = calculateRRPercent(series);
          //const phase = series?.imageTag.phase?? 0;
          const percent =
            seriesSelections.find(s => s.id === sel.id)?.percent
            ?? phase;
          return {
            id: sel.id,
            percent: String(percent),
          };
        })
      );
    }
  }, [seriesSelections]);

  useEffect(() => {
    if (segmentationStatusBySeries) {
      setSegmentationStatusMap(segmentationStatusBySeries);
    }
  }, [segmentationStatusBySeries]);

  const { waitingAction, closeLoadingModal } = useModalActions();

  useEffect(() => {
    if (segmentationStatusBySeries) return;
    if (!isSuccess || !accessToken || Series.data.length === 0) return;

    let active = true;
    const loadSegmentationStatuses = async () => {
      const results = await Promise.all(
        Series.data.map(async (item) => {
          const items = await fetch_base_info(accessToken, item.id);
          // const items = Array.isArray(raw) ? raw : [];
          const segmentationOk = items.some(
            (item) =>
              item.name.toLowerCase() === 'segmentation' &&
              item.status === 'Succeeded',
          );
          return [item.id, segmentationOk] as const;
        }),
      );
      if (!active) return;
      setSegmentationStatusMap(Object.fromEntries(results));
    };

    loadSegmentationStatuses();
    return () => {
      active = false;
    };
  }, [segmentationStatusBySeries, isSuccess, Series.data, accessToken]);

  const toggleSeriesSelection = (id: string) => {
    const series = Series.data.find(s => s.id === id);
    const selected = seriesSelections.find((s) => s.id === id);
    setSelectedSeries((prev) => {
      const existing = prev.find((item) => item.id === id);
      if (existing) {
        return prev.filter((item) => item.id !== id);
      }
      const phase = calculateRRPercent(series);
      //const phase = series?.imageTag.phase?? 0;
      return [
        ...prev,
        {
          id,
          percent: selected?.percent ?? String(phase),
        },
      ];
    });
  };

  const updateSeriesPercent = (id: string, percent: string) => {
    setSelectedSeries((prev) =>
      prev.map((item) => (item.id === id ? { ...item, percent } : item)),
    );
  };
  async function make_json(info: string[][]) {
    return {
      SERIES: info,
    };
  }
  const series_selector = async () => {
    if (!series) return;
    if(hasDuplicateIds(selectedSeries)){
      await successAction('Please make sure each series has a unique percent value.');
      return;
    }

    if (selectedSeries.length === 1) {
      await successAction('Please make sure to select at least two series.');
      return;
    }

    updateSeriesSections(selectedSeries);

    const data: string[][] = [];
    selectedSeries.map((value) => {
      data.push([value.percent, value.id]);
    });

    if(selectedSeries.length === 0) return;

    waitingAction({ message: 'Waiting' });

    const value = await send_ef_info(accessToken, {
      series_id: series.id,
      json: await make_json(data),
    });
    console.log(value, pool.parameter.data);

    await requestBufferPoolJson(
      pool,
      setBufferPool,
      accessToken,
      selectedSeriesId!,
      'parameter',
    );

    const ef_status = value[0];
    const ef_value = value[1];
    const ef_result_info = value[2];
    if (ef_status) {
      await onComplete?.(ef_result_info,ef_value, data);
    }
    closeLoadingModal();
    updateSelectAnalysis(series, 'ejectionFraction');
    close();
  };

  const hasDuplicateIds = (list: EjectionFractionInfo[]): boolean => {
  const seen = new Set<string>();
  for (const { percent } of list) {
    if (seen.has(percent)) return true;
    seen.add(percent);
  }
  return false;
};
  const handleSeriesSelect = async () => {
    series_selector();
  };

  const renderSeriesItem = (item: Series, index: number) => {
    const selected = selectedSeries.find((s) => s.id === item.id);
    const isSegmented = segmentationStatusMap[item.id] === true;

    return (
      <li
        key={item.id}
        className={`${styles.seriesItem} ${
          item.id === selectedSeriesId ? styles.selectedHighlight : ''
        }`}
      >
        <label className={styles.seriesLabel}>
          <Radio
            onChange={() => toggleSeriesSelection(item.id)}
            checked={!!selected}
            value={`option${index}`}
          />
          <span
            className={`${styles.statusDot} ${
              isSegmented ? styles.statusOk : styles.statusBad
            }`}
          />
          <div className={styles.seriesInfo}>
            <span className={styles.seriesDesc}>
              Description : {item.seriesDescription || '설명 없음'}
            </span>
            <span className={styles.seriesDesc}>
              Image Comment : {item.imageTag.imageComment || '설명 없음'}
            </span>
          </div>
        </label>
        {selected && (
          <div className={styles['eject-dropdown']}>
            <Dropdown
              id={`percent-dropdown-${item.id}`}
              placeholder={`${selected.percent}%`}
              options={options}
              value={selected.percent}
              onChange={(val) => updateSeriesPercent(item.id, val)}
            />
          </div>
        )}
      </li>
    );
  };

  const sortedSeries = isSuccess
    ? [...Series.data].sort((a, b) => {
        if (a.id === selectedSeriesId) return -1;
        if (b.id === selectedSeriesId) return 1;
        return 0;
      })
    : [];

  return (
    <div className={styles['EjectionFraction-modal-container']}>
      <div className={styles['EjectionFraction-modal-container-1']}>
        <Close
          className={styles['EjectionFraction-modal-container-icon']}
          onClick={close}
        />
      </div>

      <div className={styles['EjectionFraction-modal-container-2']}>
        <div>Please select at least one series</div>
        <div>{Series.data.length} series found</div>
      </div>

      <div className={styles['EjectionFraction-modal-container-3']}>
        {isSuccess && sortedSeries.length > 0 && (
          <ul className={styles.seriesList}>
            {Series.data.map((item, index) => renderSeriesItem(item, index))}
          </ul>
        )}
      </div>

      <div className={styles['EjectionFraction-modal-container-4']}>
        <Button
          disabled={
            selectedSeries.length === 0 ||
            selectedSeries.some((s) => !s.percent)
          }
          onClick={handleSeriesSelect}
        >
          Select
        </Button>
      </div>
    </div>
  );
};

export default EjectionFractionModalContent;
